import { useEffect, useMemo, useState } from 'react';
import { useProjectTikTokCampaigns } from '../hooks/useProjectTikTokCampaigns';
import useInvalidateProjectCampaigns from '../hooks/useInvalidateProjectCampaigns';
import { creatorbase } from '@round/api';
import UpdateTiktokCampaignModal from './UpdateTiktokCampaignModal/UpdateTiktokCampaignModal';
import ProjectCampaignsTable, {
    AddCreatorsTarget,
    CampaignTableRow,
} from '../components/ProjectCampaignsTable/ProjectCampaignsTable';
import useNonNullContext from 'Hooks/useNonNullContext';
import { OptionsContext } from 'contexts/OptionsContext/OptionsContext';
import useProjectDetails from '../useProjectDetails';
import { useProjectCampaignsStats } from '../hooks/useProjectCampaignsStats';
import { buildTiktokMusicUrl, mapStringListToArray, showNotification } from 'helpers';
import { ExpandedState } from '@tanstack/react-table';
import useRefreshProjectCampaignStats from '../hooks/useRefreshProjectCampaignStats';
import useCreateProjectDetailsPost from '../hooks/posts/useCreateProjectDetailsPost';
import { useInvalidatePosts } from '@round/creatorbase-helpers/api-hooks';
import { getProjectDetailsPostsParams } from '../hooks/posts/getProjectDetailsPostsParams';
import TiktokPosts from '../components/PostsTables/TiktokPostsTable/TiktokPosts';
import CreatePostModal from '../Posts/CreatePostModal/CreatePostModal';
import BulkCreatePostModal from '../Posts/BulkCreatePostModal/BulkCreatePostModal';
import useUrlState from 'Hooks/useUrlState';
import { isNotNullOrUndefined, isNumber } from 'utility/utility';
import DeleteCampaignModal from '../components/DeleteCampaignModal/DeleteCampaignModal';
import { useReorderProjectCampaigns } from '../hooks/useReorderProjectCampaigns';
import TikTokAudioModal from 'Modules/Plans/Audios/AudioModal/TikTokAudioModal/TikTokAudioModal';
import useCurrentUserClients from 'Modules/Plans/Clients/hooks/useCurrentUserClients';
import CampaignBriefModal from '../components/CampaignBriefModal/CampaignBriefModal';
import { formatBriefDataToWhatsAppMarkdown } from '../helpers/copyToClipboard.helpers';
import copy from 'copy-to-clipboard';
import useCopyTiktokReportToClipboard from '../hooks/copyToClipboard/useCopyTiktokReportToClipboard';
import useCopyTiktokProposalToClipboard from '../hooks/copyToClipboard/useCopyTiktokProposalToClipboard';
import useCopyTiktokAudienceProposalToClipboard from '../hooks/copyToClipboard/useCopyTiktokAudienceProposalToClipboard';
import useProjectId from '../hooks/useProjectId';
import PurchaseOrderAllocationModal from 'Modules/Plans/Finance/PurchaseOrders/PurchaseOrderAllocationModal/PurchaseOrderAllocationModal';
import AddToCampaignModal from '../components/AddCreatorsToCampaignModal/AddToCampaignModal';
import AddFromPoolModal from '../components/AddCreatorsFromPoolModal/AddFromPoolModal';
import useCampaignPurchaseOrders from '../hooks/useCampaignPurchaseOrders';

type UrlState = Partial<{
    expanded: string;
    audioId: number;
    backupExpanded: string;
    scrollToCampaign: string;
}>;

type Props = {
    onCreateCampaign: () => void;
};

const TiktokCampaigns = ({ onCreateCampaign }: Props) => {
    const [urlState, setUrlState] = useUrlState<UrlState>();
    const { currencies } = useNonNullContext(OptionsContext);

    const { project, users, team, brand } = useProjectDetails();
    const projectId = useProjectId();

    const {
        data,
        isError: hasCampaignsError,
        isInitialized: areCampaignsInitialized,
        audios,
        audioTimeSeries,
    } = useProjectTikTokCampaigns();
    const tiktokCampaigns = data?.results;
    const invalidateProjectCampaigns = useInvalidateProjectCampaigns();

    const { data: campaignsStats, loadingCampaignIds: campaignStatsLoading } = useProjectCampaignsStats(
        tiktokCampaigns?.map((c) => c.id) ?? []
    );

    const reorderCampaigns = useReorderProjectCampaigns('tiktok');

    const [isEditCampaignModalOpen, setIsEditCampaignModalOpen] = useState(false);
    const [isEditBriefModalOpen, setIsEditBriefModalOpen] = useState(false);
    const [campaignToEdit, setCampaignToEdit] = useState<CampaignTableRow | null>(null);
    const [isConfirmDeleteCampaignModalOpen, setIsConfirmDeleteCampaignModalOpen] = useState(false);
    const [campaignToDelete, setCampaignToDelete] = useState<CampaignTableRow | null>(null);

    const expandedState = useMemo(() => {
        const expanded = urlState?.expanded;
        if (!expanded) {
            return {};
        }

        return expanded.split(',').reduce(
            (acc, campaignId) => {
                acc[campaignId] = true;
                return acc;
            },
            {} as Record<string, boolean>
        );
    }, [urlState]);

    const setExpandedState = (expanded: ExpandedState) => {
        const expandedKeys = Object.keys(expanded).filter((key) => expanded[key as keyof ExpandedState]);
        setUrlState({ expanded: expandedKeys.join(',') });
    };

    const refreshAllStats = useRefreshProjectCampaignStats();
    const { createPost } = useCreateProjectDetailsPost();
    const invalidatePosts = useInvalidatePosts();

    useEffect(() => {
        if (!urlState.scrollToCampaign || !areCampaignsInitialized) {
            return;
        }

        // The scroll target's post table renders once the campaigns are in (the
        // row is expanded via the same navigation), so wait a tick for the
        // element, scroll to it, then clear the one-shot url state.
        const timeout = setTimeout(() => {
            document.getElementById(`post-table-${urlState.scrollToCampaign}`)?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
            setUrlState({ scrollToCampaign: undefined });
        }, 50);

        return () => clearTimeout(timeout);
    }, [urlState.scrollToCampaign, setUrlState, areCampaignsInitialized]);

    const campaignIds = tiktokCampaigns?.map((c) => c.id) ?? [];
    const campaignPurchaseOrders = useCampaignPurchaseOrders({ campaignIds: campaignIds });

    const tableRows: CampaignTableRow[] = useMemo(() => {
        return (
            tiktokCampaigns?.map((campaign) => {
                const currency = currencies.find((c) => c.id === campaign.currency_id);
                const teamMembers = users?.results.filter((user) => campaign.team_members.includes(user.id)) ?? [];

                const stats = campaignsStats[campaign.id]?.data ?? null;

                const audio = campaign.tiktok_details?.audio_id
                    ? audios.data[campaign.tiktok_details.audio_id] ?? null
                    : null;
                const audioUrl =
                    audio?.tiktok_id && audio.title ? buildTiktokMusicUrl(audio.title, audio.tiktok_id) : undefined;

                const audioId = campaign.tiktok_details?.audio_id;
                const audioTimeSeriesRow: CampaignTableRow['audioTimeSeries'] = (() => {
                    if (!audioId) return null;
                    const tsData = audioTimeSeries.data[audioId] ?? null;
                    if (audioTimeSeries.isError) {
                        return { status: 'error', data: tsData, error: 'Could not load time series' };
                    }
                    if (audioTimeSeries.isLoading) {
                        return { status: 'loading', data: tsData, error: null };
                    }
                    return tsData
                        ? { status: 'success', data: tsData, error: null }
                        : { status: 'idle', data: null, error: null };
                })();
                const purchaseOrders =
                    campaignPurchaseOrders.data
                        ?.filter((i) => i.campaignId === campaign.id)
                        .map((i) => i.purchaseOrder)
                        .filter(isNotNullOrUndefined) ?? [];

                return {
                    ...campaign,
                    teamMembers,
                    currency,
                    stats,
                    audioTimeSeries: audioTimeSeriesRow,
                    audioUrl,
                    purchaseOrders,
                };
            }) ?? []
        );
    }, [
        currencies,
        users,
        tiktokCampaigns,
        campaignsStats,
        audioTimeSeries.data,
        audioTimeSeries.isLoading,
        audioTimeSeries.isError,
        audios.data,
        campaignPurchaseOrders.data,
    ]);

    const { clients } = useCurrentUserClients();
    const isPaymentEnabled = !!clients.find((c) => c.id === team?.client_id)?.is_payment_enabled;

    const [isCreatePostModalOpen, setIsCreatePostModalOpen] = useState(false);
    const [isBulkCreatePostModalOpen, setIsBulkCreatePostModalOpen] = useState(false);
    const [selectedCampaign, setSelectedCampaign] = useState<CampaignTableRow | null>(null);
    const [addCreatorsTarget, setAddCreatorsTarget] = useState<AddCreatorsTarget | null>(null);

    const onCreatePost = (campaign: CampaignTableRow) => {
        setIsCreatePostModalOpen(true);
        setSelectedCampaign(campaign);
    };

    const onBulkCreatePost = (campaign: CampaignTableRow) => {
        setIsBulkCreatePostModalOpen(true);
        setSelectedCampaign(campaign);
    };

    const copyBriefToClipboard = (campaign: CampaignTableRow) => {
        const audioId = campaign.tiktok_details?.audio_id;
        const audio = audioId ? audios.data[audioId] ?? null : null;

        const formattedBriefData = formatBriefDataToWhatsAppMarkdown({
            audioUrl: audio ? buildTiktokMusicUrl(audio.title ?? '', audio.tiktok_id) : undefined,
            brandName: brand?.name,
            projectName: project?.name,
            campaignName: campaign.name,
            brief: campaign.brief,
            turnaround: campaign.brief_turnaround,
        });

        const success = copy(formattedBriefData, { format: 'text/plain' });

        if (success) {
            showNotification('Brief copied!', 'info');
        } else {
            showNotification('Could not copy brief', 'error');
        }
    };

    const expandedBackupCampaigns = mapStringListToArray(urlState.backupExpanded).map(Number);
    const toggleExpandedBackupCampaigns = (campaignId: number) => {
        if (expandedBackupCampaigns.includes(campaignId)) {
            setUrlState({ backupExpanded: expandedBackupCampaigns.filter((id) => id !== campaignId).toString() });
            return;
        }

        setUrlState({ backupExpanded: expandedBackupCampaigns.concat(campaignId).toString() });
    };

    const copyReportTableToClipboard = useCopyTiktokReportToClipboard();
    const copyProposalTableToClipboard = useCopyTiktokProposalToClipboard();
    const copyAudienceProposalTableToClipboard = useCopyTiktokAudienceProposalToClipboard();

    const [allocationCampaignId, setAllocationCampaignId] = useState<number | null>(null);

    return (
        <>
            <ProjectCampaignsTable
                data={tableRows}
                onEdit={(c) => {
                    setCampaignToEdit(c);
                    setIsEditCampaignModalOpen(true);
                }}
                onEditBrief={(c) => {
                    setCampaignToEdit(c);
                    setIsEditBriefModalOpen(true);
                }}
                onCopyBrief={copyBriefToClipboard}
                onCopyReport={copyReportTableToClipboard}
                onCopyProposal={copyProposalTableToClipboard}
                onCopyAudienceProposal={copyAudienceProposalTableToClipboard}
                onCreatePost={onCreatePost}
                onBulkCreatePost={onBulkCreatePost}
                onCreateCampaign={onCreateCampaign}
                onDeleteCampaign={(campaign) => {
                    setIsConfirmDeleteCampaignModalOpen(true);
                    setCampaignToDelete(campaign);
                }}
                onAddCreatorsToCampaign={(campaign, source) => {
                    setAddCreatorsTarget({ campaignId: campaign.id, source });
                }}
                onAllocate={(row) => setAllocationCampaignId(row.id)}
                hasError={hasCampaignsError}
                expandedState={expandedState}
                onExpandedChange={setExpandedState}
                renderSubComponent={(row) => (
                    <TiktokPosts
                        campaign={row.original}
                        isPaymentEnabled={isPaymentEnabled}
                        isBackupExpanded={expandedBackupCampaigns.includes(row.original.id)}
                        onBackupExpandToggle={() => toggleExpandedBackupCampaigns(row.original.id)}
                        onAddCreatorsToCampaign={(campaign) =>
                            setAddCreatorsTarget({ campaignId: campaign.id, source: 'explore' })
                        }
                        onCreatePost={() => onCreatePost(row.original)}
                    />
                )}
                isLoading={!areCampaignsInitialized}
                statsLoading={campaignStatsLoading}
                arePurchaseOrdersLoading={campaignPurchaseOrders.isLoading}
                onDragEnd={reorderCampaigns}
                onAudioGrowthCellClick={(row) => {
                    if (row.tiktok_details?.audio_id) {
                        setUrlState({ audioId: row.tiktok_details.audio_id });
                        return;
                    }
                }}
            />

            <UpdateTiktokCampaignModal
                isOpen={isEditCampaignModalOpen}
                closeModal={() => {
                    setIsEditCampaignModalOpen(false);
                    setCampaignToEdit(null);
                }}
                campaign={campaignToEdit as creatorbase.TiktokCampaign | null}
            />

            <DeleteCampaignModal
                isOpen={isConfirmDeleteCampaignModalOpen}
                campaign={campaignToDelete}
                onClose={() => {
                    setIsConfirmDeleteCampaignModalOpen(false);
                    setCampaignToDelete(null);
                }}
            />

            <CampaignBriefModal
                isOpen={isEditBriefModalOpen}
                closeModal={() => {
                    setIsEditBriefModalOpen(false);
                    setCampaignToEdit(null);
                }}
                campaign={campaignToEdit}
            />

            <CreatePostModal
                isOpen={isCreatePostModalOpen}
                closeModal={() => {
                    setIsCreatePostModalOpen(false);
                    setSelectedCampaign(null);
                }}
                campaign={tiktokCampaigns?.find((c) => c.id === selectedCampaign?.id)}
                createPost={createPost}
            />

            <BulkCreatePostModal
                isOpen={isBulkCreatePostModalOpen}
                closeModal={() => {
                    setIsBulkCreatePostModalOpen(false);
                    setSelectedCampaign(null);
                }}
                campaign={tiktokCampaigns?.find((c) => c.id === selectedCampaign?.id)}
            />

            <TikTokAudioModal
                isOpen={Boolean(urlState.audioId)}
                onClose={() => setUrlState({ audioId: undefined })}
                audioId={Number(urlState.audioId)}
            />

            <PurchaseOrderAllocationModal
                campaignId={allocationCampaignId}
                isOpen={isNumber(allocationCampaignId)}
                onClose={(hasUpdated) => {
                    if (hasUpdated && allocationCampaignId) {
                        invalidateProjectCampaigns('tiktok');
                    }

                    setAllocationCampaignId(null);
                }}
            />

            <AddToCampaignModal
                key={`add-to-campaign-${addCreatorsTarget?.campaignId}`}
                isOpen={addCreatorsTarget?.source === 'explore'}
                onClose={(hasUpdated) => {
                    if (hasUpdated && addCreatorsTarget) {
                        invalidatePosts(getProjectDetailsPostsParams(addCreatorsTarget.campaignId));
                        refreshAllStats(addCreatorsTarget.campaignId, 'tiktok');
                    }
                    setAddCreatorsTarget(null);
                }}
                campaign={tiktokCampaigns?.find(
                    (c): c is creatorbase.TiktokCampaign =>
                        c.id === addCreatorsTarget?.campaignId && c.platform === 'tiktok'
                )}
                projectId={projectId}
            />

            <AddFromPoolModal
                key={`add-from-pool-${addCreatorsTarget?.campaignId}`}
                isOpen={addCreatorsTarget?.source === 'pool'}
                onClose={(hasUpdated) => {
                    if (hasUpdated && addCreatorsTarget) {
                        invalidatePosts(getProjectDetailsPostsParams(addCreatorsTarget.campaignId));
                        refreshAllStats(addCreatorsTarget.campaignId, 'tiktok');
                    }
                    setAddCreatorsTarget(null);
                }}
                campaign={tiktokCampaigns?.find(
                    (c): c is creatorbase.TiktokCampaign =>
                        c.id === addCreatorsTarget?.campaignId && c.platform === 'tiktok'
                )}
            />
        </>
    );
};

export default TiktokCampaigns;
